import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaHeart, FaMobile, FaPalette } from "react-icons/fa";
import { useTranslation } from "react-i18next";

gsap.registerPlugin(ScrollTrigger);

const featureConfig = [
    { key: "design", icon: FaHeart },
    { key: "responsive", icon: FaMobile },
    { key: "aesthetics", icon: FaPalette }
];

const About = () => {
    const { t } = useTranslation();
    const sectionRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            const animateOnScroll = (selector, options = {}) => {
                gsap.utils.toArray(selector).forEach((el) => {
                    gsap.fromTo(el,
                        { opacity: 0, y: 60, scale: 0.97 },
                        {
                            opacity: 1, y: 0, scale: 1, duration: 1.2, ease: "power2.out",
                            scrollTrigger: { trigger: el, start: "top 85%", end: "top 60%", toggleActions: "play none none reverse", scrub: 1.2 },
                            ...options
                        }
                    );
                });
            };
            animateOnScroll(".title");
            animateOnScroll(".subtitle");
            animateOnScroll(".intro-text");
            animateOnScroll(".philosophy-card");
            animateOnScroll(".feature", { stagger: 0.3 });

            // Green Line Animation
            gsap.fromTo(".accent-line",
                { height: "0%" },
                {
                    height: "100%",
                    duration: 1.5,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: ".philosophy-card",
                        start: "top 80%", // Starts when top of card is at 80% viewport height
                        end: "center center", // Ends when center of card is at center
                        scrub: 1.5 // Smooth scrubbing
                    }
                }
            );

        }, sectionRef);
        return () => ctx.revert();
    }, []);

    const philosophyTitle = t("about.slides.philosophy.title");
    const philosophyParagraphs = t("about.slides.philosophy.paragraphs", { returnObjects: true, defaultValue: [] });

    return (
        <div ref={sectionRef} className="about-container max-w-[1300px] m-auto px-6 sm:px-10 py-20 font-Crimson">
            <div className="text-center mb-20">
                <h1 className="title text-4xl md:text-6xl font-bold mb-16 text-slate-800">
                    {t("about.title")}
                </h1>
                <span className="subtitle block text-2xl md:text-3xl text-slate-500 mt-4 tracking-wider font-light">
                    {t("about.subtitle")}
                </span>
                <p className="intro-text text-lg md:text-xl max-w-3xl mx-auto mt-10 leading-relaxed text-slate-600">
                    {t("about.intro")}
                </p>
            </div>

            {/* Single Philosophy Card */}
            <div className="philosophy-card max-w-4xl mx-auto mb-20">
                <div className="bg-white/80 backdrop-blur-md rounded-2xl p-8 md:p-12 border border-slate-200 shadow-sm relative overflow-hidden">
                    {/* Decorative accent */}
                    <div className="accent-line absolute top-0 left-0 w-2 h-full bg-lime-500" />

                    <h2 className="text-3xl md:text-4xl font-semibold mb-8 text-slate-800 leading-tight">
                        {philosophyTitle}
                    </h2>

                    <div className="space-y-6 text-slate-600 text-lg leading-relaxed">
                        {philosophyParagraphs.map((paragraph, idx) => (
                            <p key={idx}>{paragraph}</p>
                        ))}
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-1 gap-12 items-center mb-0">
                <div className="space-y-8">
                    <div className="grid sm:grid-cols-2 gap-6 lg:grid-cols-3 lg:my-20">
                        {featureConfig.map(({ key, icon: Icon }) => (
                            <div
                                key={key}
                                className="feature bg-white/5 backdrop-blur-md rounded-xl p-6 border-slate-200 hover:shadow-md transition-shadow duration-300"
                            >
                                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                                    <Icon className="text-lime-500" />
                                    {t(`about.features.${key}.title`)}
                                </h4>
                                <p className="text-sm text-slate-600 leading-relaxed">{t(`about.features.${key}.desc`)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default About;
